import React, { useState } from 'react';
import {
  CheckCircle,
  Clock,
  XCircle,
  AlertCircle,
  FileText,
  MessageSquare,
  Link,
  Phone,
  Info,
  ExternalLink,
  ImageIcon,
  VideoIcon,
  FileText as FileIcon,
  Type,
  LayoutTemplate,
  AlignLeft,
  MousePointer2,
  Copy,
  Check,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { Card, CardHeader, CardContent } from '@/components/Card';
import { useTemplate } from '@/features/chatbots';
import { AUTH_BODY_TRANSLATIONS } from '@/features/whats-app-crm/utils/language';
import { useNavigate, useParams, useOutletContext } from 'react-router-dom';
import { Loader } from '@/components';
import { formatDateTime } from '@/utils/dateFormatter';


// ─── Types ───────────────────────────────────────────────────────────────────

interface MetaButton {
  type: 'quick_reply' | 'url' | 'phone_number' | 'otp' | 'copy_code' | 'flow' | 'voice_call';
  text?: string;
  url?: string;
  phone_number?: string;
  otp_type?: string;
  example?: string;
  label?: string;
  title?: string;
}

interface MetaComponent {
  type: 'header' | 'body' | 'footer' | 'buttons';
  format?: 'text' | 'image' | 'video' | 'document';
  text?: string;
  buttons?: MetaButton[];
  add_security_recommendation?: boolean;
  example?: {
    body_text?: string[][];
    header_handle?: string[];
    header_text?: string[] | string[][];
    header_text_named_params?: Array<{ param_name: string; example: string }> | Array<Array<{ param_name: string; example: string }>>;
  };
}

const getPlaceholderKey = (placeholder: string) => placeholder.replace(/{{|}}/g, '').trim();

const normalizeNamedParams = (
  params?: Array<{ param_name: string; example: string }> | Array<Array<{ param_name: string; example: string }>>
) => {
  if (!params) return [];
  return Array.isArray(params[0])
    ? (params[0] as Array<{ param_name: string; example: string }>)
    : (params as Array<{ param_name: string; example: string }>);
};

const buildExampleMap = (
  matches: string[],
  positionalExamples?: string[],
  namedParams?: Array<{ param_name: string; example: string }> | Array<Array<{ param_name: string; example: string }>>
) => {
  const exampleMap: Record<string, string> = {};
  const uniquePositionKeys: string[] = [];

  matches.forEach((match) => {
    const key = getPlaceholderKey(match);
    if (!exampleMap[key] && /^\d+$/.test(key)) {
      if (!uniquePositionKeys.includes(key)) {
        uniquePositionKeys.push(key);
      }
      const example = positionalExamples?.[Number(key) - 1] ?? positionalExamples?.[uniquePositionKeys.indexOf(key)];
      if (example) {
        exampleMap[key] = example;
      }
    }
  });

  normalizeNamedParams(namedParams).forEach((param) => {
    if (param?.param_name && param.example) {
      exampleMap[param.param_name] = param.example;
    }
  });

  return exampleMap;
};

function getAuthBodyText(language: string, withSecurity: boolean): string {
  const translations = AUTH_BODY_TRANSLATIONS[language] || AUTH_BODY_TRANSLATIONS['en'];
  return withSecurity ? translations.withSecurity : translations.default;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatDate(date?: string): string {
  if (!date) return '—';
  return formatDateTime(date);
}

function parseComponents(components?: MetaComponent[], category: string = '', language: string = 'en') {
  if (!components) return { header: undefined, body: undefined, footer: undefined, buttons: [], bodyText: '', variables: [], headerVariables: [] };

  const isAuth = category.toUpperCase() === 'AUTHENTICATION';
  const header = components.find((c) => c.type?.toLowerCase() === 'header');
  const body = components.find((c) => c.type?.toLowerCase() === 'body');
  const footer = components.find((c) => c.type?.toLowerCase() === 'footer');
  const buttonsComp = components.find((c) => c.type?.toLowerCase() === 'buttons');

  const bodyText = isAuth
    ? (body?.text || getAuthBodyText(language, Boolean(body?.add_security_recommendation)))
    : body?.text ?? '';
  const variableMatches = isAuth ? [] : bodyText.match(/{{[^}]+}}/g) ?? [];
  const bodyExampleMap = buildExampleMap(
    variableMatches,
    body?.example?.body_text?.[0],
    (body?.example as any)?.body_text_named_params
  );
  const variables = variableMatches.map((match) => {
    const key = getPlaceholderKey(match);
    return {
      name: match,
      key,
      fallback: bodyExampleMap[key] ?? '',
    };
  });

  const headerText = header?.text ?? '';
  const headerVariableMatches = headerText.match(/{{[^}]+}}/g) ?? [];
  const headerExample = header?.example as any;
  const headerTextExample = headerExample?.header_text?.[0];
  const headerExampleMap = buildExampleMap(
    headerVariableMatches,
    Array.isArray(headerTextExample) ? headerTextExample : headerTextExample ? [headerTextExample] : undefined,
    headerExample?.header_text_named_params
  );
  const headerVariables = headerVariableMatches.map((match) => {
    const key = match.replace(/{{|}}/g, '').trim();
    return { name: match, key, fallback: headerExampleMap[key] ?? '' };
  });

  const headerValueMap = headerVariables.reduce<Record<string, string>>((acc, variable) => {
    if (variable.key) {
      acc[variable.key] = variable.fallback;
    }
    return acc;
  }, {});

  const headerPreviewText = headerText.replace(/{{([^}]+)}}/g, (match, rawKey) => {
    const key = rawKey.trim();
    return headerValueMap[key] || match;
  });

  const buttons = buttonsComp?.buttons?.map((btn) => ({
    ...btn,
    text:
      btn.text ||
      btn.title ||
      btn.label ||
      (btn.type === 'otp' || btn.type === 'copy_code' ? 'Copy code' : ''),
  })) ?? [];

  return {
    header,
    body,
    footer,
    buttons,
    bodyText,
    variables,
    headerVariables,
    headerPreviewText,
  };
}


// ─── Sub-components ──────────────────────────────────────────────────────────

const statusConfig = {
  draft: {
    label: 'Draft',
    icon: FileText,
    bannerClass: 'bg-gray-50 border-gray-200 dark:bg-gray-800 dark:border-gray-700',
    iconBg: 'bg-gray-500',
    textClass: 'text-gray-800 dark:text-gray-200',
    descClass: 'text-gray-600 dark:text-gray-400',
    description: 'Template is in draft mode and can be edited',
    badgeClass: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
  },
  submitted: {
    label: 'Pending Review',
    icon: Clock,
    bannerClass: 'bg-yellow-50 border-yellow-200 dark:bg-yellow-900/10 dark:border-yellow-800',
    iconBg: 'bg-yellow-500',
    textClass: 'text-yellow-800 dark:text-yellow-300',
    descClass: 'text-yellow-700 dark:text-yellow-400',
    description: 'Template submitted to Meta — awaiting approval',
    badgeClass: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
  },
  approved: {
    label: 'Approved',
    icon: CheckCircle,
    bannerClass: 'bg-green-50 border-green-200 dark:bg-green-900/10 dark:border-green-800',
    iconBg: 'bg-green-500',
    textClass: 'text-green-800 dark:text-green-300',
    descClass: 'text-green-700 dark:text-green-400',
    description: 'Approved by Meta — ready to send to users',
    badgeClass: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  },
  rejected: {
    label: 'Rejected',
    icon: XCircle,
    bannerClass: 'bg-red-50 border-red-200 dark:bg-red-900/10 dark:border-red-800',
    iconBg: 'bg-red-500',
    textClass: 'text-red-800 dark:text-red-300',
    descClass: 'text-red-700 dark:text-red-400',
    description: 'Rejected by Meta — fix issues and resubmit',
    badgeClass: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  },
  disabled: {
    label: 'Disabled',
    icon: AlertCircle,
    bannerClass: 'bg-gray-50 border-gray-200 dark:bg-gray-800 dark:border-gray-700',
    iconBg: 'bg-gray-400',
    textClass: 'text-gray-700 dark:text-gray-300',
    descClass: 'text-gray-500 dark:text-gray-400',
    description: 'Template has been disabled',
    badgeClass: 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400',
  },
} as const;

const buttonTypeConfig = {
  quick_reply: { label: 'Quick Reply', icon: MessageSquare, colorClass: 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-900/20 dark:text-blue-300 dark:border-blue-800' },
  url: { label: 'URL', icon: Link, colorClass: 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-300 dark:border-blue-800' },
  phone_number: { label: 'Phone', icon: Phone, colorClass: 'bg-purple-50 text-purple-700 border-purple-200 dark:bg-purple-900/20 dark:text-purple-300 dark:border-purple-800' },
  otp: { label: 'OTP', icon: Copy, colorClass: 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/20 dark:text-amber-300 dark:border-amber-800' },
  copy_code: { label: 'Copy Code', icon: Copy, colorClass: 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/20 dark:text-amber-300 dark:border-amber-800' },
};



// ─── Components Breakdown ─────────────────────────────────────────────────────

function ComponentsBreakdown({ components }: { components: ReturnType<typeof parseComponents> }) {
  const { header, bodyText, footer, buttons, variables, headerVariables, headerPreviewText } = components;

  const getHeaderIcon = () => {
    switch (header?.format?.toLowerCase()) {
      case 'image': return { icon: ImageIcon, bg: 'bg-emerald-50 dark:bg-emerald-900/30', text: 'text-emerald-600 dark:text-emerald-400' };
      case 'video': return { icon: VideoIcon, bg: 'bg-blue-50 dark:bg-blue-900/30', text: 'text-blue-600 dark:text-blue-400' };
      case 'document': return { icon: FileIcon, bg: 'bg-orange-50 dark:bg-orange-900/30', text: 'text-orange-600 dark:text-orange-400' };
      default: return { icon: Type, bg: 'bg-gray-100 dark:bg-gray-800', text: 'text-gray-600 dark:text-gray-400' };
    }
  };

  const hCfg = getHeaderIcon();

  return (
    <div className="space-y-6">
      {/* Header */}
      {header && (
        <Card>
          <CardHeader className="flex flex-row items-center gap-4 py-4">
            <div className="w-10 h-10 rounded-sm bg-purple-50 dark:bg-purple-600/30 flex items-center justify-center shrink-0">
              <LayoutTemplate className="w-5 h-5 text-purple-600 dark:text-purple-400" />
            </div>
            <div className="flex-1">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-base font-semibold text-gray-900 dark:text-white">Header</h2>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Media or text shown at the top of the message.</p>
                </div>
                <span className="text-[10px] bg-gray-100 dark:bg-gray-800 text-gray-500 px-2 py-0.5 rounded border border-gray-200 dark:border-gray-700 font-bold uppercase tracking-widest">
                  {header.format ?? 'text'}
                </span>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {header.text ? (
              <>
                <div className="p-3 bg-gray-50 dark:bg-black/20 rounded-lg border border-gray-100 dark:border-gray-800">
                  <p className="text-[14px] font-medium text-gray-900 dark:text-gray-100 leading-relaxed">{headerPreviewText || header.text}</p>
                </div>
                {headerVariables?.length > 0 && (
                  <div className="mt-4 space-y-3">
                    <p className="text-[11px] font-bold text-gray-400 uppercase tracking-widest px-1">Header Variable Examples</p>
                    <div className="rounded-xl bg-gray-50 dark:bg-black/20 border border-gray-100 dark:border-gray-800 overflow-hidden">
                      <table className="w-full text-left text-[12px]">
                        <thead>
                          <tr className="bg-gray-100/50 dark:bg-white/5 border-b border-gray-100 dark:border-gray-800">
                            <th className="px-4 py-3 font-bold text-gray-400 uppercase tracking-widest">Variable</th>
                            <th className="px-4 py-3 font-bold text-gray-400 uppercase tracking-widest">Example Value</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                          {headerVariables.map((v, i) => (
                            <tr key={i} className="hover:bg-white/50 dark:hover:bg-white/1 transition-colors">
                              <td className="px-4 py-3 font-mono text-blue-600 dark:text-blue-400 font-bold">{v.name}</td>
                              <td className="px-4 py-3 text-gray-600 dark:text-gray-300 font-medium">
                                {v.fallback || <em className="text-gray-400 opacity-40">Not defined</em>}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="flex items-center gap-2 p-4 bg-gray-50 dark:bg-black/20 rounded-lg text-gray-500 text-sm italic border border-gray-100 dark:border-gray-800">
                <hCfg.icon className="w-5 h-5 opacity-30" />
                The header contains {header.format} media example
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Body */}
      <Card>
        <CardHeader className="flex flex-row items-center gap-4 py-4">
          <div className="w-10 h-10 rounded-sm bg-emerald-50 dark:bg-emerald-600/30 flex items-center justify-center shrink-0">
            <AlignLeft className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
          </div>
          <div className="flex-1">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-base font-semibold text-gray-900 dark:text-white">Body Message</h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">The primary content of your template message.</p>
              </div>
              {variables.length > 0 && (
                <span className="text-[10px] bg-blue-500/10 text-blue-600 dark:text-blue-400 px-2 py-0.5 rounded font-bold uppercase tracking-widest border border-blue-500/20">
                  {variables.length} variable{variables.length > 1 ? 's' : ''}
                </span>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="p-4 bg-white dark:bg-black/20 rounded-xl border border-gray-100 dark:border-gray-800 shadow-inner">
            <p className="text-[15px] text-gray-800 dark:text-gray-200 leading-relaxed break-words whitespace-pre-wrap">
              {bodyText.split(/({{[^}]+}})/).map((part, i) =>
                /^{{[^}]+}}$/.test(part) ? (
                  <span key={i} className="bg-blue-500/10 text-blue-600 dark:text-blue-400 px-1.5 py-0.5 rounded font-mono text-[13px] font-bold">
                    {part}
                  </span>
                ) : (
                  <span key={i}>{part}</span>
                )
              )}
            </p>
          </div>

          {variables.length > 0 && (
            <div className="space-y-3">
              <p className="text-[11px] font-bold text-gray-400 uppercase tracking-widest px-1">Variable Examples</p>
              <div className="rounded-xl bg-gray-50 dark:bg-black/20 border border-gray-100 dark:border-gray-800 overflow-hidden">
                <table className="w-full text-left text-[12px]">
                  <thead>
                    <tr className="bg-gray-100/50 dark:bg-white/5 border-b border-gray-100 dark:border-gray-800">
                      <th className="px-4 py-3 font-bold text-gray-400 uppercase tracking-widest">Variable</th>
                      <th className="px-4 py-3 font-bold text-gray-400 uppercase tracking-widest">Example Value</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                    {variables.map((v, i) => (
                      <tr key={i} className="hover:bg-white/50 dark:hover:bg-white/1 transition-colors">
                        <td className="px-4 py-3 font-mono text-blue-600 dark:text-blue-400 font-bold">{v.name}</td>
                        <td className="px-4 py-3 text-gray-600 dark:text-gray-300 font-medium">{v.fallback || <em className="text-gray-400 opacity-40">Not defined</em>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Footer & Buttons Group */}
      <div className="flex flex-col gap-6">
        {footer?.text && (
          <Card className="flex flex-col">
            <CardHeader className="flex flex-row items-center gap-4 py-4">
              <div className="w-10 h-10 rounded-sm bg-gray-100 dark:bg-gray-600/30 flex items-center justify-center shrink-0">
                <Type className="w-5 h-5 text-gray-600 dark:text-gray-400" />
              </div>
              <div className="flex-1">
                <h2 className="text-base font-semibold text-gray-900 dark:text-white">Footer</h2>
                <p className="text-[10px] text-gray-500 dark:text-gray-400 uppercase tracking-widest font-bold">Optional Text</p>
              </div>
            </CardHeader>
            <CardContent className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
              <div className="text-center p-3 bg-gray-50 dark:bg-black/20 rounded-lg border border-gray-100 dark:border-gray-800">
                <p className="text-sm text-gray-500 dark:text-gray-400 font-medium leading-relaxed">{footer.text}</p>
              </div>
            </CardContent>
          </Card>
        )}

        {buttons.length > 0 && (
          <Card className="flex flex-col">
            <CardHeader className="flex flex-row items-center gap-4 py-4">
              <div className="w-10 h-10 rounded-sm bg-orange-50 dark:bg-orange-600/30 flex items-center justify-center shrink-0">
                <MousePointer2 className="w-5 h-5 text-orange-600 dark:text-orange-400" />
              </div>
              <div className="flex-1">
                <div className="flex items-center justify-between">
                  <h2 className="text-base font-semibold text-gray-900 dark:text-white">Buttons</h2>
                  <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">{buttons.length} Total</span>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400">Interaction points for users.</p>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-2.5">
                {buttons.map((btn, i) => {
                  const cfg = buttonTypeConfig[btn.type as keyof typeof buttonTypeConfig] ?? { label: btn.type, icon: MessageSquare, colorClass: 'bg-gray-50 text-gray-600 border-gray-100' };
                  const BtnIcon = cfg.icon;
                  return (
                    <div key={i} className={`flex items-center justify-between px-3.5 py-3 rounded-lg border transition-all hover:shadow-sm ${cfg.colorClass}`}>
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="p-1.5 bg-white/50 dark:bg-white/5 rounded">
                          <BtnIcon className="w-4 h-4 shrink-0" />
                        </div>
                        <span className="text-sm font-bold truncate">{btn.text}</span>
                      </div>
                      {btn.url && (
                        <a href={btn.url} target="_blank" rel="noreferrer" className="shrink-0 p-2 hover:bg-black/5 dark:hover:bg-white/5 rounded-md transition-colors">
                          <ExternalLink className="w-4 h-4 opacity-60" />
                        </a>
                      )}
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}



function DetailRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-100 dark:border-gray-700 last:border-b-0">
      <span className="text-xs text-gray-500 dark:text-gray-400">{label}</span>
      <span className="text-xs font-medium text-gray-800 dark:text-gray-200">{value}</span>
    </div>
  );
}

function CopyIdRow({ id }: { id: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(id).then(() => {
      setCopied(true);
      toast.success('Template ID copied!');
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-100 dark:border-gray-700">
      <span className="text-xs text-gray-500 dark:text-gray-400">Template ID</span>
      <div className="flex items-center gap-1.5">
        <span className="text-xs font-mono text-gray-500 dark:text-gray-400" title={id}>
          {id.slice(0, 5)}....{id.slice(-5)}
        </span>
        <button
          onClick={handleCopy}
          className="p-1 rounded transition-all text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
          title={copied ? 'Copied!' : 'Copy full ID'}
        >
          {copied ? (
            <Check className="w-3 h-3 text-green-500" />
          ) : (
            <Copy className="w-3 h-3" />
          )}
        </button>
      </div>
    </div>
  );
}



export default function ViewTemplatePage() {
  const navigate = useNavigate();
  const { templateId } = useParams();
  const { setHeaderActions, setCustomHeader, setWhatsAppValues } = useOutletContext<any>() || {};
  const { data: templateData, isFetching } = useTemplate(
    templateId || null,
    { enabled: Boolean(templateId) }
  );

  const template = templateData && templateData?.data;

  const category = (template?.category || template?.metaPayload?.category || '').toString();
  const components = templateData && parseComponents(
    template?.metaPayload?.components,
    category,
    template?.language || template?.metaPayload?.language || 'en'
  );
  const status = template && statusConfig[template?.status!];

  React.useEffect(() => {
    if (!setCustomHeader || !setHeaderActions) return;

    if (template) {
      setCustomHeader({
        heading: template.metaTemplateName,
        description: `v${template.version} · ${template.category} · ${template.language}`,
        showBackButton: true,
        onBack: () => navigate(-1),
      });
    } else {
      setCustomHeader({
        heading: "Loading Template...",
        description: "Fetching details",
        showBackButton: true,
        onBack: () => navigate(-1),
      });
    }

    if (setWhatsAppValues && templateData?.data) {
      setWhatsAppValues(templateData.data);
    }

    return () => {
      setCustomHeader?.(null);
      setHeaderActions?.(null);
    };
  }, [setCustomHeader, setHeaderActions, template, navigate, templateData, setWhatsAppValues]);



  if (isFetching) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader />
      </div>
    );
  }

  if (!template) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <AlertCircle className="w-6 h-6 text-red-500" />
        <p className="ml-2 text-sm text-gray-500">Template not found</p>
      </div>
    );
  }


  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 font-sans">
      <div className="flex flex-col gap-6">


        {/* Rejection Reason */}
        {template?.rejectionReason && (
          <div className="rounded-xl border border-red-200 dark:border-red-800 overflow-hidden">
            <div className="flex items-center gap-2 px-4 py-2.5 bg-red-50 dark:bg-red-900/20 border-b border-red-200 dark:border-red-800">
              <XCircle className="w-4 h-4 text-red-500" />
              <span className="text-xs font-semibold text-red-700 dark:text-red-400 uppercase tracking-wider">Rejection Reason from Meta</span>
            </div>
            <div className="px-4 py-3 bg-white dark:bg-gray-800">
              <p className="text-sm text-red-700 dark:text-red-300 leading-relaxed">{template.rejectionReason}</p>
            </div>
          </div>
        )}

        {/* Two-column grid */}
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-5">

          {/* Left column */}
          <div >
            <ComponentsBreakdown components={components!} />
          </div>

          {/* Right sidebar */}
          <div className="space-y-4">

            {/* Details */}
            <Card>
              <CardHeader className="flex flex-row items-center gap-4 py-4 bg-gray-50/30 dark:bg-white/5">
                <div className="w-10 h-10 rounded-sm bg-blue-50 dark:bg-blue-900/30 flex items-center justify-center shrink-0">
                  <Info className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                </div>
                <div className="flex-1">
                  <h2 className="text-sm font-bold text-gray-900 dark:text-white uppercase tracking-tight">Details</h2>
                  <p className="text-[10px] text-gray-500 dark:text-gray-400 uppercase tracking-widest font-bold">Metadata</p>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                <CopyIdRow id={template._id} />
                <DetailRow label="Category" value={<span className="capitalize">{template?.category}</span>} />
                <DetailRow label="Language" value={<span className="uppercase">{template?.language}</span>} />
                <DetailRow label="Version" value={`v${template?.version}`} />
                {template?.metaStatus && (
                  <DetailRow
                    label="Meta Status"
                    value={
                      <span className={`inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full font-semibold ${status?.badgeClass}`}>
                        {template.metaStatus}
                      </span>
                    }
                  />
                )}
              </CardContent>
            </Card>

            {/* Timeline */}
            <Card>
              <CardHeader className="flex flex-row items-center gap-4 py-4 bg-gray-50/30 dark:bg-white/5">
                <div className="w-10 h-10 rounded-sm bg-gray-100 dark:bg-gray-800 flex items-center justify-center shrink-0">
                  <Clock className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                </div>
                <div className="flex-1">
                  <h2 className="text-sm font-bold text-gray-900 dark:text-white uppercase tracking-tight">Timeline</h2>
                  <p className="text-[10px] text-gray-500 dark:text-gray-400 uppercase tracking-widest font-bold">History</p>
                </div>
              </CardHeader>
              <CardContent className="px-6 py-6 border-t border-gray-100 dark:border-gray-800">
                <div className="relative pl-6">
                  <div className="absolute left-[3px] top-1 bottom-1 w-[2px] bg-gray-100 dark:bg-gray-800" />
                  {[
                    { label: 'Created', date: template?.createdAt, dot: 'bg-gray-300' },
                    ...(template?.submittedAt ? [{ label: 'Submitted to Meta', date: template?.submittedAt, dot: 'bg-amber-400' }] : []),
                    { label: 'Last Updated', date: template?.updatedAt, dot: 'bg-blue-400' },
                    ...(template?.approvedAt ? [{ label: 'Approved by Meta', date: template?.approvedAt, dot: 'bg-green-500' }] : []),
                  ].map(({ label, date, dot }, i) => (
                    <div key={i} className="flex items-start gap-4 mb-6 last:mb-0 relative">
                      <div className={`absolute -left-[27px] top-1.5 w-2.5 h-2.5 rounded-full ${dot} border-2 border-white dark:border-gray-950 z-10 shadow-sm`} />
                      <div>
                        <p className="text-[12px] font-bold text-gray-900 dark:text-white leading-none">{label}</p>
                        <p className="text-[10px] text-gray-400 dark:text-gray-500 mt-1.5 font-medium">{formatDate(date)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

          </div>
        </div>
      </div>
    </div>
  );
}


